import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePracticeDetail } from '../../hooks/usePracticeDetail';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { PracticeDetailHeader } from '../../components/accounts/practice/PracticeDetailHeader';
import {
    DEFAULT_TAB,
    getVisibleDetailTabs,
    isTabAccessible,
    type DetailTabId
} from '../../components/accounts/practice/detailTabs';
import {
    DEFAULT_DETAIL_SECTION,
    isDetailSection,
    sectionForField,
    type DetailSectionId
} from '../../components/accounts/practice/details/detailSections';
import { OverviewTab } from '../../components/accounts/practice/overview/OverviewTab';
import { DetailsTab } from '../../components/accounts/practice/details/DetailsTab';
import { PatientsTab } from '../../components/accounts/practice/patients/PatientsTab';
import { CommunicationsTab } from '../../components/accounts/practice/communications/CommunicationsTab';
import { PracticeUsersTab } from '../../components/accounts/practice/users/PracticeUsersTab';
import { PracticeBillingTab } from '../../components/accounts/practice/billing/PracticeBillingTab';
import { PracticePricingTab } from '../../components/accounts/practice/billing/PracticePricingTab';
import { PracticeInvoicesTabAccounts } from '../../components/accounts/practice/billing/PracticeInvoicesTabAccounts';
import { usePracticeDetailsForm } from '../../components/accounts/practice/details/usePracticeDetailsForm';

// Validated against isTabAccessible so ?tab=billing without access (or garbage)
// falls back to the default. `?tab=calls` is a legacy alias for old bookmarks.
function readTabFromSearch(search: string, canAccessBilling: boolean): DetailTabId {
    const raw = new URLSearchParams(search).get('tab');
    if (raw === 'calls') return 'communications';
    return raw && isTabAccessible(raw, canAccessBilling) ? raw : DEFAULT_TAB;
}

// Same convention as readTabFromSearch, one param over - a hand-edited or
// stale `?section=` falls back to the default rather than a blank pane.
function readSectionFromSearch(search: string): DetailSectionId {
    const raw = new URLSearchParams(search).get('section');
    return raw && isDetailSection(raw) ? raw : DEFAULT_DETAIL_SECTION;
}

export function PracticeDetail() {
    const { practiceId } = useParams<{ practiceId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { hasPermission } = useMyPermissions();
    const canAccessBilling = hasPermission('billing.get');
    const visibleTabs = getVisibleDetailTabs(canAccessBilling);
    const [activeTab, setActiveTab] = useState<DetailTabId>(() =>
        readTabFromSearch(location.search, canAccessBilling)
    );

    // Keep activeTab in sync with the URL - mirrors PatientFiles's ?tab= sync,
    // which is what makes browser back/forward move between tabs.
    useEffect(() => {
        const next = readTabFromSearch(location.search, canAccessBilling);
        setActiveTab((prev) => (next === prev ? prev : next));
    }, [location.search, canAccessBilling]);

    const handleTabChange = (tab: DetailTabId) => {
        setActiveTab(tab);
        const params = new URLSearchParams(location.search);
        if (tab === DEFAULT_TAB) {
            params.delete('tab');
        } else {
            params.set('tab', tab);
        }
        const search = params.toString();
        // `replace` mirrors PatientFiles's tab switches - back/forward moves
        // between *pages*, not between this page's tabs.
        navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    };

    // Details tab's left-rail section - same `?section=` + `replace` pattern as
    // `?tab=` above, one level deeper (only meaningful while activeTab === 'details').
    const [activeSection, setActiveSection] = useState<DetailSectionId>(() =>
        readSectionFromSearch(location.search)
    );

    useEffect(() => {
        const next = readSectionFromSearch(location.search);
        setActiveSection((prev) => (next === prev ? prev : next));
    }, [location.search]);

    const handleSectionChange = useCallback(
        (section: DetailSectionId) => {
            setActiveSection(section);
            const params = new URLSearchParams(location.search);
            if (section === DEFAULT_DETAIL_SECTION) {
                params.delete('section');
            } else {
                params.set('section', section);
            }
            const search = params.toString();
            navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
        },
        [location.pathname, location.search, navigate]
    );

    const { practice, isLoading, isError, refetch } = usePracticeDetail(practiceId);
    const form = usePracticeDetailsForm(practiceId ?? '', practice);

    // Shared by the header's onAddField pills and the Details tab's error/save
    // banners - switches to the field's section first, then scrolls once mounted.
    const pendingScrollFieldRef = useRef<string | null>(null);

    const scrollToFieldKeyed = useCallback(
        (fieldKey: string) => {
            const targetSection = sectionForField(fieldKey);
            if (targetSection === activeSection) {
                setTimeout(() => form.scrollToField(fieldKey), 0);
                return;
            }
            pendingScrollFieldRef.current = fieldKey;
            handleSectionChange(targetSection);
        },
        [activeSection, form, handleSectionChange]
    );

    useEffect(() => {
        const fieldKey = pendingScrollFieldRef.current;
        if (!fieldKey) return;
        pendingScrollFieldRef.current = null;
        const timeoutId = setTimeout(() => form.scrollToField(fieldKey), 0);
        return () => clearTimeout(timeoutId);
    }, [activeSection, form]);

    // Only the browser-tab title is set here - the visible title comes from
    // PracticeDetailHeader's name card (this route hides WelcomeBanner - see App.tsx).
    usePageTitle(practice?.name ?? null);

    if (!practiceId) {
        return <Navigate to="/accounts/practices" replace />;
    }

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }

    if (isError || !practice) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <div className="text-sm text-red-500">Failed to load this practice</div>
                <button
                    onClick={() => refetch()}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <PracticeDetailHeader
                practice={practice}
                onAddField={scrollToFieldKeyed}
                tabs={visibleTabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />
            {activeTab === 'overview' && (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-8">
                    <OverviewTab
                        practiceId={practice.id}
                        onGoToCommunicationsTab={() => handleTabChange('communications')}
                    />
                </div>
            )}
            {activeTab === 'details' && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <DetailsTab
                        form={form}
                        practice={practice}
                        activeSection={activeSection}
                        onSectionChange={handleSectionChange}
                        onFixField={scrollToFieldKeyed}
                    />
                </div>
            )}
            {activeTab === 'patients' && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <PatientsTab practiceName={practice.name} />
                </div>
            )}
            {activeTab === 'communications' && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <CommunicationsTab practiceId={practice.id} />
                </div>
            )}
            {activeTab === 'users' && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <PracticeUsersTab practiceId={practice.id} practiceName={practice.name} />
                </div>
            )}
            {activeTab === 'billing' && canAccessBilling && (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-8 pb-4">
                    <PracticeBillingTab practiceId={practice.id} />
                </div>
            )}
            {activeTab === 'pricing' && canAccessBilling && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <PracticePricingTab practiceId={practice.id} practiceName={practice.name} />
                </div>
            )}
            {activeTab === 'invoices' && canAccessBilling && (
                <div className="flex-1 min-h-0 flex flex-col px-8 pb-4">
                    <PracticeInvoicesTabAccounts practiceId={practice.id} />
                </div>
            )}
        </div>
    );
}
